/**
 * Helpers for ops booking assignment: pick a provider, then a driver from that provider.
 */

export const BUBAKII_PROVIDER_ID = "__BUBAKII__";

export const BUBAKII_PROVIDER = {
  id: BUBAKII_PROVIDER_ID,
  name: "Bubakii",
  ownerType: "bubakii",
  contactPerson: null,
  phone: null,
};

export const CREATE_DRIVER_OPTION_VALUE = "__NEW_DRIVER__";

export function isBubakiiProvider(provider) {
  if (!provider) return false;
  return (
    provider.id === BUBAKII_PROVIDER_ID ||
    (provider.ownerType === "bubakii" && !provider.partnerId)
  );
}

/**
 * @param {Object|null} assignedTeam - booking.assignedTeam snapshot
 * @returns {Object|null} provider option or null when the caller should resolve from the driver
 */
export function providerFromAssignedTeam(assignedTeam) {
  if (!assignedTeam) return null;

  if (assignedTeam.partnerId) {
    return {
      id: assignedTeam.partnerId,
      name: assignedTeam.partnerName || "Provider",
      ownerType: assignedTeam.ownerType || "third_party",
    };
  }

  const ownerType =
    assignedTeam.ownerType || assignedTeam.primaryDriver?.ownerType;
  if (ownerType === "bubakii") {
    return { ...BUBAKII_PROVIDER };
  }

  return null;
}

/**
 * @param {Object|null} driver
 * @param {Object|null} [partner]
 * @returns {Object|null}
 */
export function providerFromDriver(driver, partner = null) {
  if (!driver) return null;
  if (driver.ownerType === "third_party" && driver.partnerId) {
    return {
      id: driver.partnerId,
      name: partner?.name || driver.partner?.name || "Provider",
      ownerType: "third_party",
    };
  }
  return { ...BUBAKII_PROVIDER };
}

/**
 * Filters for GET /drivers when a provider is selected.
 * @param {Object|null} provider
 * @returns {Object|null}
 */
export function driverFiltersForProvider(provider) {
  if (!provider) return null;
  if (isBubakiiProvider(provider)) {
    return { ownerType: "bubakii", isActive: true };
  }
  return { partnerId: provider.id, isActive: true };
}

/**
 * Filters for GET /vehicles when a provider is selected.
 * Bubakii → ownerType bubakii; third-party → partnerId.
 * @param {Object|null} provider
 * @param {{ status?: string }} [options]
 * @returns {Object|null}
 */
export function vehicleFiltersForProvider(provider, options = {}) {
  if (!provider) return null;
  const filters = isBubakiiProvider(provider)
    ? { ownerType: "bubakii" }
    : { partnerId: provider.id, ownerType: "third_party" };
  if (options.status) {
    filters.status = options.status;
  }
  return filters;
}

/**
 * @param {Array} partners
 * @param {string} search
 * @returns {Array}
 */
export function filterProviders(partners = [], search = "") {
  const list = [{ ...BUBAKII_PROVIDER }, ...partners];
  const term = search.trim().toLowerCase();
  if (!term) return list;

  return list.filter((provider) => {
    const name = provider.name?.toLowerCase() || "";
    const contact = provider.contactPerson?.toLowerCase() || "";
    const phone = provider.phone || "";
    return (
      name.includes(term) ||
      contact.includes(term) ||
      phone.includes(search.trim())
    );
  });
}

export function matchesProviderSearch(provider, search) {
  if (!search || !search.trim()) return true;
  const term = search.trim().toLowerCase();
  return (
    provider.name?.toLowerCase().includes(term) ||
    provider.contactPerson?.toLowerCase().includes(term) ||
    (provider.phone || "").includes(search.trim())
  );
}

export function matchesDriverSearch(driver, search) {
  if (!search || !search.trim()) return true;
  const term = search.trim().toLowerCase();
  return (
    driver.name?.toLowerCase().includes(term) ||
    (driver.phone || "").includes(search.trim()) ||
    driver.licenseNumber?.toLowerCase().includes(term)
  );
}
